import { type ReactElement, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { AlertTriangle } from "@/components/icons"
import { Button } from "@/components/ui/button"

type HeavyPreviewGateProps = {
  fileName: string
  fileExtension: string
  children: ReactElement
}

/**
 * Placeholder shown instead of a heavy document preview (docx, xlsx) when
 * gating is active. The actual viewer is only mounted after clicking "Load anyway".
 */
export function HeavyPreviewGate({
  fileName,
  fileExtension,
  children,
}: HeavyPreviewGateProps): ReactElement {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)

  // Reset gate when a different file is shown in the same panel
  useEffect(() => {
    setLoaded(false)
  }, [fileName])

  if (loaded) {
    return children
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <AlertTriangle aria-hidden className="text-muted-foreground size-10" />
      <p className="text-muted-foreground text-sm">
        {t("citation.heavy_preview_warning", {
          defaultValue: "This {{ext}} document may be slow to load.",
          ext: fileExtension.toUpperCase(),
        })}
      </p>
      {fileName ? (
        <p className="text-muted-foreground max-w-[300px] truncate text-xs">{fileName}</p>
      ) : null}
      <Button variant="outline" onClick={() => setLoaded(true)}>
        {t("citation.load_preview", { defaultValue: "Load anyway" })}
      </Button>
    </div>
  )
}
